import { useEffect, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Button,
  Card,
  HeadingText,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { CommunicationComponent } from 'components/profile-settings/communication-preference/communication.component';
import { PersonalInfoComponent } from 'components/profile-settings/personal-Info.component';
import { AccountSetupSubmitErrorModal } from 'components/modals/components/account-setup-submit-error.modal';
import { useAccountSetupForm } from 'hooks/use-account-setup-form.hook';
import { sendAccountSetupScreenLoadedEvent } from 'utils/analytics-events';

interface AccountSetupFormProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export const AccountSetupForm = ({
  onComplete,
  onSkip,
}: AccountSetupFormProps) => {
  const { formatMessage } = useIntl();
  const [showSubmitErrorModal, setShowSubmitErrorModal] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const {
    formData,
    isUpdating,
    isCompleting,
    fetchErrorCode,
    handleInputChange,
    handleSubmit,
    initialOptions,
  } = useAccountSetupForm({
    onComplete,
    onSubmitError: () => setShowSubmitErrorModal(true),
  });

  const handleSkipClick = async () => {
    if (isSkipping) return; // Prevent multiple clicks

    setIsSkipping(true);
    try {
      if (onSkip) {
        await onSkip();
      } else {
        onComplete();
      }
    } catch (error) {
      // On error, still proceed
      onComplete();
    } finally {
      setIsSkipping(false);
    }
  };

  useDocumentTitle(formatMessage({ id: 'STR_ACCOUNT_SETUP' }));

  // Track screen loaded event
  useEffect(() => {
    sendAccountSetupScreenLoadedEvent();
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    if (isSkipping) {
      e.preventDefault();
      return;
    }
    handleSubmit(e);
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <StackLayout
        orientation="vertical"
        horizontalAlignment="center"
        css={{
          alignSelf: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          backgroundColor: '$surfaceBackgroundSecondary',
          minHeight: '100vh',
          '@mobile': { maxWidth: '100%' },
          '@mobileLandscape': { maxWidth: '100%' },
        }}
      >
        <Card.Elevated
          css={{
            backgroundColor: 'white',
            maxWidth: '708px',
            marginY: '$twoAndHalf',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '90vh',
          }}
        >
          <Card.Section
            css={{
              paddingBottom: 0,
              maxHeight: '91px',
            }}
          >
            <HeadingText
              level="1"
              size="xl"
              css={{
                marginBottom: '$half',
                textAlign: 'center',
                marginTop: '$one',
              }}
            >
              {formatMessage({ id: 'STR_ACCOUNT_SETUP_TITLE' })}
            </HeadingText>
          </Card.Section>
          <Card.Section
            css={{
              padding: '0 $two',
              paddingTop: 0,
              flexGrow: 1,
            }}
          >
            <PersonalInfoComponent
              preferredFirstName={formData.preferredFirstName}
              alwaysEdit
              hideButtons
              disableUnsavedModal
              onlyPreferredName
              noPadding
              showBorder={false}
              description={formatMessage({
                id: 'STR_PREFFERED_FIRST_NAME_DESCRIPTION',
              })}
              onInputChange={handleInputChange}
            />
            {fetchErrorCode === 500 ? (
              <StackLayout css={{ marginTop: '$one' }}>
                <HeadingText level="2" size="md" css={{ marginBottom: '$one' }}>
                  {formatMessage({ id: 'STR_COMMUNICATION_PREFERENCES' })}
                </HeadingText>
                <ParagraphText css={{ color: '$onSurfaceError' }}>
                  {formatMessage({
                    id: 'STR_COMMUNICATION_PREFERENCES_SETUP_500',
                  })}
                </ParagraphText>
              </StackLayout>
            ) : (
              <CommunicationComponent
                title="STR_COMMUNICATION_PREFERENCES"
                initialOptions={initialOptions}
                alwaysEdit
                hideButtons
                noPadding
                showBorder={false}
                disableUnsavedModal
                onInputChange={handleInputChange}
                isAccountSetup
              />
            )}
          </Card.Section>
          <Card.Section
            css={{
              padding: '$one 0',
              paddingBottom: '24px',
              marginTop: 'auto',
              maxHeight: '121px',
            }}
          >
            <StackLayout
              orientation="horizontal"
              css={{
                justifyContent: 'flex-end',
                marginTop: '$oneAndHalf',
                borderTop: '1px solid $onSurfaceBorderSubdued',
                padding: '$one',
              }}
            >
              <Button
                type="submit"
                css={{
                  height: '$three',
                }}
                loading={isUpdating || isCompleting}
                disabled={isUpdating || isCompleting || isSkipping}
              >
                {formatMessage({ id: 'STR_CONTINUE' })}
              </Button>
            </StackLayout>
          </Card.Section>
        </Card.Elevated>
        <AccountSetupSubmitErrorModal
          open={showSubmitErrorModal}
          onClose={() => setShowSubmitErrorModal(false)}
          onSkip={handleSkipClick}
          onRetry={() => setShowSubmitErrorModal(false)}
          isSkipping={isSkipping}
        />
      </StackLayout>
    </form>
  );
};
